import { finalizeAcknowledgedChecklistMedia } from './ChecklistMediaAcknowledgement';
import { publishChecklistMediaTerminal } from '@/services/checklist/ChecklistMediaTerminalEvents';
import { planSupersededMediaNoteConflict, authorizedSuccessorObservation, withoutSuccessorObservationIntent, hasConfirmedMediaSuccessionBaseline } from './ChecklistMediaSuccessionContract';
import { Platform } from 'react-native';
import * as FileSystem from 'expo-file-system/legacy';
import nacl from 'tweetnacl';
import naclUtil from 'tweetnacl-util';
import { databaseService } from '@/services/databaseService';
import { checklistAnswerValue } from '@/services/checklist/checklistObservations';
import { fetchFile, ensureDirectoryExists } from '@/utils/file/fileHelpers';
import { downloadRemoteImageToFile } from '@/utils/file/downloadRemoteImageIOS';
import { getSyncClientBuild } from './SyncCanaryFeatureService';
import { sha512Hex } from './PendingOperationTerminalization';
import { MEDIA_CONFLICT_SCHEMA, hasOpenMediaObservation, hasMediaCancellation, hasPendingMediaObservation, mediaPayloadHash, sameMediaRow, sameMediaIdentityAndImage,
  type ChecklistMediaAlternative, type ChecklistMediaChosenObject } from './ChecklistMediaConflictContract';
import { assertPendingSyncSessionCurrent, createPendingSyncSupabaseClient,
  isPendingSyncSessionSnapshotStateCurrent, type PendingSyncSessionSnapshot } from './PendingSyncSession';

type Row = Record<string, any>;

export async function checklistMediaBytes(body: Blob | ArrayBuffer): Promise<Uint8Array> {
  if (body instanceof ArrayBuffer) return new Uint8Array(body);
  if (typeof body.arrayBuffer === 'function') return new Uint8Array(await body.arrayBuffer());
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Não foi possível ler a mídia.'));
    reader.onload = () => {
      try { resolve(naclUtil.decodeBase64(String(reader.result).split(',')[1])); } catch (error) { reject(error); }
    };
    reader.readAsDataURL(body);
  });
}

const hashBytes = (bytes: Uint8Array) => Array.from(nacl.hash(bytes)).map(b => b.toString(16).padStart(2, '0')).join('');
const pathInScope = (path: string, companyId: string, checklistId: string): boolean => {
  const [folder, company, ...rest] = path.split('/');
  return ['checklists-photos', 'checklists-signatures', 'checklists-marcacoes'].includes(folder) &&
    company === companyId && !rest.includes('..') &&
    (rest[0] === checklistId || rest.length === 1 &&
      (rest[0]?.startsWith(`${checklistId}-`) || rest[0]?.startsWith(`${checklistId}.`)));
};
const imageKind = (bytes: Uint8Array) => bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff
  ? { extension: 'jpg', mime: 'image/jpeg' }
  : bytes[0] === 0x89 && String.fromCharCode(...bytes.slice(1, 4)) === 'PNG'
    ? { extension: 'png', mime: 'image/png' }
    : String.fromCharCode(...bytes.slice(0, 4)) === 'RIFF' && String.fromCharCode(...bytes.slice(8, 12)) === 'WEBP'
      ? { extension: 'webp', mime: 'image/webp' } : null;

async function downloadObjectBytes(storage: any, path: string, publicUrl: string,
  checklistId: string, snapshot: PendingSyncSessionSnapshot): Promise<Uint8Array | null> {
  if (!pathInScope(path, snapshot.empresaId, checklistId) ||
      storage.getPublicUrl(path).data.publicUrl !== publicUrl) return null;
  await assertPendingSyncSessionCurrent(snapshot);
  if (Platform.OS === 'web') {
    const { data, error } = await storage.download(path);
    await assertPendingSyncSessionCurrent(snapshot);
    return !error && data ? checklistMediaBytes(data) : null;
  }
  if (!FileSystem.cacheDirectory) throw new Error('Diretório temporário de mídia indisponível.');
  const directory = `${FileSystem.cacheDirectory}checklist-media-verification/`;
  await ensureDirectoryExists(directory);
  const temporaryUri = `${directory}${Date.now()}-${Math.random().toString(16).slice(2)}.bin`;
  try {
    // O download nativo evita Response.blob(), incompatível com ArrayBuffer no React Native.
    await assertPendingSyncSessionCurrent(snapshot);
    await downloadRemoteImageToFile(publicUrl, temporaryUri, FileSystem);
    await assertPendingSyncSessionCurrent(snapshot);
    const bytes = await checklistMediaBytes(await fetchFile(temporaryUri));
    await assertPendingSyncSessionCurrent(snapshot);
    return bytes;
  } finally {
    await FileSystem.deleteAsync(temporaryUri, { idempotent: true });
  }
}

async function inspectChosenObject(pending: Row, remote: Row, snapshot: PendingSyncSessionSnapshot): Promise<ChecklistMediaChosenObject | null> {
  const storage = createPendingSyncSupabaseClient(snapshot).storage.from(pending.bucket);
  const path = typeof remote.valor === 'string' ? remote.valor.split(`/storage/v1/object/public/${pending.bucket}/`)[1] : null;
  if (!path || !pathInScope(path, snapshot.empresaId, pending.item_id) ||
      storage.getPublicUrl(path).data.publicUrl !== remote.valor) return null;
  const bytes = await downloadObjectBytes(storage, path, remote.valor, pending.item_id, snapshot);
  if (!bytes) return null;
  const kind = imageKind(bytes);
  return kind && bytes.length ? { schema: 'checklist-media-server-object/v1', bucket: pending.bucket, path,
    publicUrl: remote.valor, contentType: kind.mime, byteLength: bytes.length, bytesSha512: hashBytes(bytes) } : null;
}

async function alternativeCopyMatches(alternative: ChecklistMediaAlternative): Promise<boolean> {
  const bytes = await checklistMediaBytes(await fetchFile(alternative.localCopyUri));
  return bytes.length === alternative.byteLength && hashBytes(bytes) === alternative.bytesSha512;
}

async function preserveAlternative(pending: Row, value: Row, archive: Row, remote: Row,
  snapshot: PendingSyncSessionSnapshot): Promise<ChecklistMediaAlternative> {
  const bytes = await checklistMediaBytes(await fetchFile(pending.local_uri));
  const kind = imageKind(bytes);
  if (!kind || !bytes.length) throw new Error('Alternativa local sem bytes de imagem verificáveis.');
  const digest = hashBytes(bytes);
  const name = `${sha512Hex(archive.id).slice(0, 48)}-${digest.slice(0, 32)}.${kind.extension}`;
  let localCopyUri: string;
  if (Platform.OS === 'web') {
    // A chave não contém checklistId e não entra na varredura de órfãos do checklist.
    localCopyUri = `checklist-photos/retained-conflicts/${snapshot.empresaId}/${pending.user_id}/${name}`;
    await databaseService.bulkUpsert('checklist_photos', [{ id: localCopyUri,
      base64: naclUtil.encodeBase64(bytes), created_at: Date.now(), updated_at: Date.now() }]);
  } else {
    if (!FileSystem.documentDirectory) throw new Error('Diretório durável de mídia indisponível.');
    const directory = `${FileSystem.documentDirectory}retained-media-conflicts/${snapshot.empresaId}/${pending.user_id}/`;
    await ensureDirectoryExists(directory);
    localCopyUri = directory + name;
    await FileSystem.writeAsStringAsync(localCopyUri, naclUtil.encodeBase64(bytes), { encoding: FileSystem.EncodingType.Base64 });
  }
  await assertPendingSyncSessionCurrent(snapshot);
  const copy = await checklistMediaBytes(await fetchFile(localCopyUri));
  if (copy.length !== bytes.length || hashBytes(copy) !== digest) throw new Error('Cópia durável da alternativa não confirmou os bytes.');
  const storage = createPendingSyncSupabaseClient(snapshot).storage.from(pending.bucket);
  const downloaded = await downloadObjectBytes(storage, pending.path, pending.prepared_public_url, pending.item_id, snapshot);
  if (!downloaded || hashBytes(downloaded) !== digest) throw new Error('Objeto original não confirmou os bytes da alternativa.');
  return { schema: 'checklist-media-alternative/v1', companyId: snapshot.empresaId, checklistId: pending.item_id,
    fieldId: pending.campo_id, physicalFieldId: value.campo_id ?? null, snapshotFieldId: value.snapshot_field_id ?? null,
    valueId: value.id, pendingId: pending.id, originalAuthor: pending.user_id, resolutionActor: snapshot.userId,
    localCopyUri, originalLocalUri: pending.local_uri, bucket: pending.bucket, path: pending.path,
    preparedPublicUrl: pending.prepared_public_url, contentType: kind.mime, byteLength: bytes.length,
    bytesSha512: digest, chosenRemote: remote };
}

/** Só o conflito terminal comprovado autoriza combinar colunas ou escolher a imagem do servidor. */
export async function resolveTerminalChecklistMedia(pending: Row, snapshot: PendingSyncSessionSnapshot): Promise<'not_applicable' | 'pending' | 'resolved'> {
  if (pending.item_type !== 'CHECKLIST' || pending.empresa_id !== snapshot.empresaId || !pending.user_id ||
      !pending.campo_id || !pending.prepared_public_url) return 'not_applicable';
  if (!pathInScope(pending.path, snapshot.empresaId, pending.item_id)) return 'not_applicable';
  const client = createPendingSyncSupabaseClient(snapshot);
  const storage = client.storage.from(pending.bucket);
  if (storage.getPublicUrl(pending.path).data.publicUrl !== pending.prepared_public_url) return 'not_applicable';
  const values = await databaseService.listItems<Row>('guincho_valores_checklist', { checklist_id: pending.item_id });
  const matches = values.filter(row => row.empresa_id === snapshot.empresaId &&
    (row.snapshot_field_id === pending.campo_id || row.campo_id === pending.campo_id));
  if (matches.length !== 1) return 'not_applicable';
  const value = matches[0];
  if (value.valor !== pending.prepared_public_url && value.valor !== pending.local_uri) return 'not_applicable';
  const parent = await databaseService.readItem<Row>('guincho_checklists', pending.item_id);
  if (!parent || parent.empresa_id !== snapshot.empresaId || parent.retired_at) return 'not_applicable';
  const archives = await databaseService.listItems<Row>('accounted_pending_operations', { item_id: pending.item_id });
  const candidates = archives.filter(archive => {
    if (archive.empresa_id !== snapshot.empresaId || archive.accounting_state !== 'conflict_materialized' ||
        archive.operation !== 'checklist_partial_update' || archive.collection !== 'guincho_valores_checklist') return false;
    try {
      const data = JSON.parse(archive.data);
      return data.payload?.id === value.id && data.payload.valor === pending.prepared_public_url;
    } catch { return false; }
  });
  if (!candidates.length) return 'not_applicable';
  if (candidates.length !== 1) return 'pending';
  let archive = candidates[0];
  const original = JSON.parse(archive.data);
  if (pending.baseline_captured !== 1 || !pending.expected_remote_updated_at ||
      !Number.isFinite(Date.parse(pending.expected_remote_updated_at)) ||
      !['optimistic_lock_failed', 'expected_updated_at_mismatch'].includes(archive.reason) ||
      original.idempotency_key !== original.payload.idempotency_key ||
      mediaPayloadHash(original.payload) !== archive.payload_sha512 ||
      (original.payload.expected_updated_at !== pending.expected_remote_updated_at &&
        !hasConfirmedMediaSuccessionBaseline(pending, original.payload, archives))) return 'pending';
  const conflict = await databaseService.readItem<Row>('sync_conflicts', archive.id.replace(/^accounted:/, 'conflict:'));
  if (!conflict || !['open', 'blocked'].includes(conflict.status) || conflict.user_id !== archive.user_id ||
      conflict.empresa_id !== snapshot.empresaId || conflict.payload_sha512 !== archive.payload_sha512 ||
      conflict.reason !== archive.reason || !conflict.id.startsWith(`conflict:${conflict.pending_operation_id}:`) ||
      conflict.id.split(':').at(-1) !== archive.payload_sha512.slice(0, 24) ||
      JSON.stringify(JSON.parse(conflict.payload)) !== JSON.stringify(JSON.parse(archive.data).payload) ||
      mediaPayloadHash(JSON.parse(conflict.payload)) !== archive.payload_sha512) return 'pending';
  const openConflicts = await databaseService.listItems<Row>('sync_conflicts', { empresa_id: snapshot.empresaId });
  const operations = await databaseService.listItems<Row>('pending_operations');
  if (Object.prototype.hasOwnProperty.call(original.payload, 'observacao') &&
      (value.observacao ?? null) !== original.payload.observacao) {
    const readback = await client.from('guincho_valores_checklist').select('*').eq('id', value.id)
      .eq('empresa_id', snapshot.empresaId).eq('checklist_id', pending.item_id).maybeSingle();
    await assertPendingSyncSessionCurrent(snapshot);
    const exclusions = await databaseService.listItems<Row>('sync_tombstones', { empresa_id: snapshot.empresaId });
    const linkedImages = await databaseService.listItems<Row>('pending_images', { item_id: pending.item_id });
    if (!readback.error && readback.data && planSupersededMediaNoteConflict(pending, value, readback.data,
        archives, openConflicts, operations, parent, exclusions, linkedImages)) {
      return await finalizeAcknowledgedChecklistMedia({ pending, value, publicUrl: pending.prepared_public_url!, snapshot })
        ? 'resolved' : 'pending';
    }
  }
  const originalPhysicalField = original.media_resolution?.originalValue?.campo_id;
  const ownsNote = Object.prototype.hasOwnProperty.call(original.payload, 'observacao');
  const ownNote = authorizedSuccessorObservation(pending, value, original.payload, archives);
  if (ownsNote && !ownNote) return 'pending';
  const intents = withoutSuccessorObservationIntent(operations, openConflicts, conflict, original.payload, ownNote,
    [pending.campo_id, value.campo_id, value.snapshot_field_id, originalPhysicalField]);
  const ids = [pending.item_id, pending.campo_id, value.id, value.campo_id, originalPhysicalField, value.snapshot_field_id, parent.template_id].filter(Boolean);
  const tombstones = await databaseService.listItems<Row>('sync_tombstones', { empresa_id: snapshot.empresaId });
  const images = await databaseService.listItems<Row>('pending_images', { item_id: pending.item_id });
  const queued = images.find(row => row.id === pending.id);
  if (!sameMediaRow(queued, pending) || images.filter(row => row.item_type === 'CHECKLIST' && ids.includes(row.campo_id)).length !== 1 ||
      hasPendingMediaObservation(intents.operations, value, pending, [originalPhysicalField]) || hasOpenMediaObservation(intents.conflicts, value, pending, [originalPhysicalField]) ||
      hasMediaCancellation(openConflicts, pending) ||
      tombstones.some(row => row.empresa_id === snapshot.empresaId && ids.includes(row.entity_id)) ||
      operations.some(row => row.operation === 'delete' && ids.includes(row.item_id))) return 'pending';
  for (const operation of operations.filter(row => row.item_id === pending.item_id && row.operation === 'checklist_partial_update')) {
    let answers: Row;
    try { answers = typeof operation.data === 'string' ? JSON.parse(operation.data) : operation.data; } catch { return 'pending'; }
    if (!answers) return 'pending';
    const fields = Object.keys(answers).filter(fieldId => ids.includes(fieldId));
    if (fields.length && (fields.length !== 1 || operation.id !== conflict.pending_operation_id ||
        operation.user_id !== pending.user_id || operation.empresa_id !== pending.empresa_id ||
        operation.collection !== 'guincho_valores_checklist' ||
        checklistAnswerValue(answers[fields[0]]) !== pending.prepared_public_url)) return 'pending';
  }
  await assertPendingSyncSessionCurrent(snapshot);
  const remoteParent = await client.from('guincho_checklists').select('id,empresa_id,snapshot_id,template_id,retired_at')
    .eq('id', pending.item_id).eq('empresa_id', snapshot.empresaId).maybeSingle();
  const confirmedParent = remoteParent.data as Row | null;
  if (remoteParent.error || !confirmedParent || ['id', 'empresa_id', 'snapshot_id', 'template_id', 'retired_at'].some(key =>
      (confirmedParent[key] ?? null) !== (parent[key] ?? null))) return 'pending';
  let validatedSnapshotPhysicalField: string | null = null;
  if (value.snapshot_field_id) {
    const field = await client.from('guincho_checklist_snapshot_fields').select('id,empresa_id,snapshot_id,original_campo_id')
      .eq('id', value.snapshot_field_id).eq('empresa_id', snapshot.empresaId).maybeSingle();
    if (field.error || !field.data || field.data.id !== value.snapshot_field_id || field.data.empresa_id !== snapshot.empresaId ||
        field.data.snapshot_id !== parent.snapshot_id || (value.campo_id && field.data.original_campo_id !== value.campo_id)) return 'pending';
    validatedSnapshotPhysicalField = field.data.original_campo_id ?? null;
  }
  const readRemote = async (): Promise<Row | null> => {
    const result = await client.from('guincho_valores_checklist').select('id,empresa_id,checklist_id,campo_id,snapshot_field_id,valor,observacao,created_at,updated_at')
      .eq('id', value.id).eq('empresa_id', snapshot.empresaId).eq('checklist_id', pending.item_id).maybeSingle();
    await assertPendingSyncSessionCurrent(snapshot);
    const row = result.data;
    if (result.error) throw result.error;
    return row && row.id === value.id && row.empresa_id === snapshot.empresaId && row.checklist_id === pending.item_id &&
      (row.snapshot_field_id ?? null) === (value.snapshot_field_id ?? null) &&
      (value.snapshot_field_id ? (!row.campo_id || row.campo_id === value.campo_id) : row.campo_id === value.campo_id) &&
      typeof row.updated_at === 'string' && Number.isFinite(Date.parse(row.updated_at)) ? row : null;
  };
  let remote = await readRemote();
  if (!remote) return 'pending';
  let data = JSON.parse(archive.data);
  let resolution: Row = data.media_resolution;
  if (ownsNote && !authorizedSuccessorObservation(pending, value, original.payload, archives,
      resolution && resolution.phase !== 'retry_conflict' ? resolution.preparedRemote : remote)) return 'pending';
  if (resolution && (resolution.schema !== MEDIA_CONFLICT_SCHEMA || resolution.pendingId !== pending.id ||
      !resolution.originalValue)) return 'pending';
  if (resolution && !sameMediaRow(resolution.originalValue, value)) {
    const differences = [...new Set([...Object.keys(resolution.originalValue), ...Object.keys(value)])].filter(key =>
      (resolution.originalValue[key] ?? null) !== (value[key] ?? null));
    // Realtime pode remover o campo físico redundante depois da RPC histórica confirmada.
    const canonicalSnapshot = resolution.kind === 'combine' && resolution.phase === 'retry_applied' &&
      ['applied', 'historical'].includes(resolution.terminalResult?.result) &&
      resolution.terminalResult?.value_id === value.id && resolution.terminalResult?.input_index === 0 &&
      value.snapshot_field_id && value.snapshot_field_id === resolution.originalValue.snapshot_field_id &&
      validatedSnapshotPhysicalField && resolution.originalValue.campo_id === validatedSnapshotPhysicalField &&
      value.campo_id == null && remote.campo_id == null;
    if (!differences.length || !differences.every(key => ['observacao', 'updated_at'].includes(key) || key === 'campo_id' && canonicalSnapshot) ||
        !differences.every(key => (value[key] ?? null) === (remote![key] ?? null)) ||
        remote.valor !== pending.prepared_public_url) return 'pending';
  }
  if (resolution?.kind === 'server_wins' && resolution.alternative &&
      !sameMediaIdentityAndImage(resolution.alternative.chosenRemote, remote)) {
    const alternative = resolution.alternative as ChecklistMediaAlternative;
    // Uma nova imagem do servidor renova somente a escolha, mantendo a cópia e a decisão anterior.
    if (!sameMediaIdentityAndImage({ ...alternative.chosenRemote, valor: remote.valor }, remote) ||
        !await alternativeCopyMatches(alternative)) return 'pending';
    const chosenObject = await inspectChosenObject(pending, remote, snapshot);
    if (!chosenObject) return 'pending';
    const previousDecisions = [...(resolution.previousDecisions ?? []), {
      actor: resolution.actor, alternative, chosenObject: resolution.chosenObject ?? null,
    }];
    resolution = { ...resolution, actor: snapshot.userId,
      alternative: { ...alternative, chosenRemote: remote }, chosenObject, previousDecisions, phase: 'preserved' };
    const nextData = JSON.stringify({ ...data, media_resolution: resolution });
    if (!await databaseService.updateItemIfUnchanged('accounted_pending_operations', archive.id, { data: nextData }, archive, true,
      () => isPendingSyncSessionSnapshotStateCurrent(snapshot))) return 'pending';
    archive = { ...archive, data: nextData };
    data = JSON.parse(nextData);
  }
  if (!resolution || resolution.phase === 'retry_conflict' ||
      resolution.phase === 'retry_applied' && remote.valor !== pending.prepared_public_url) {
    const previousAttempts = resolution ? [...(resolution.previousAttempts ?? []), { payload: resolution.payload, terminalResult: resolution.terminalResult }] : [];
    if (remote.valor === pending.expected_remote_value && resolution?.phase !== 'retry_applied') {
      const payload = { ...data.payload, expected_updated_at: remote.updated_at,
        idempotency_key: `media-rebase:${sha512Hex(`${archive.id}:${remote.updated_at}`).slice(0, 96)}` };
      resolution = { schema: MEDIA_CONFLICT_SCHEMA, kind: 'combine', pendingId: pending.id,
        originalValue: value, actor: snapshot.userId, payload, preparedRemote: remote, previousAttempts, phase: 'prepared' };
    } else {
      if (ownsNote) return 'pending';
      // Outra imagem exige acesso real aos bytes escolhidos, nunca apenas URL/info.
      const chosenObject = await inspectChosenObject(pending, remote, snapshot);
      if (!chosenObject) return 'pending';
      const alternative = await preserveAlternative(pending, value, archive, remote, snapshot);
      resolution = { schema: MEDIA_CONFLICT_SCHEMA, kind: 'server_wins', pendingId: pending.id,
        originalValue: value, actor: snapshot.userId, alternative, chosenObject, previousAttempts, phase: 'preserved' };
    }
    // Persistir antes da RPC congela a identidade da nova tentativa, inclusive se a resposta se perder.
    const nextData = JSON.stringify({ ...data, media_resolution: resolution });
    if (!await databaseService.updateItemIfUnchanged('accounted_pending_operations', archive.id, { data: nextData }, archive, true,
      () => isPendingSyncSessionSnapshotStateCurrent(snapshot))) return 'pending';
    archive = { ...archive, data: nextData };
    data = JSON.parse(nextData);
  }
  if (resolution.kind === 'combine') {
    const payload = resolution.payload;
    if (!payload || payload.id !== value.id || payload.valor !== pending.prepared_public_url ||
        (ownsNote ? payload.observacao !== original.payload.observacao : Object.prototype.hasOwnProperty.call(payload, 'observacao')) ||
        payload.idempotency_key === data.payload.idempotency_key ||
        resolution.preparedRemote?.valor !== pending.expected_remote_value ||
        payload.expected_updated_at !== resolution.preparedRemote?.updated_at) return 'pending';
    if (resolution.phase !== 'retry_applied') {
      // As esperas de preparação não autorizam enviar depois de exclusão, cancelamento ou nova intenção local.
      const currentPending = await databaseService.readItem<Row>('pending_images', pending.id);
      const currentValue = await databaseService.readItem<Row>('guincho_valores_checklist', value.id);
      const currentParent = await databaseService.readItem<Row>('guincho_checklists', parent.id);
      const currentArchive = await databaseService.readItem<Row>('accounted_pending_operations', archive.id);
      const currentConflict = await databaseService.readItem<Row>('sync_conflicts', conflict.id);
      const currentImages = await databaseService.listItems<Row>('pending_images', { item_id: pending.item_id });
      const currentOperations = await databaseService.listItems<Row>('pending_operations');
      const currentTombstones = await databaseService.listItems<Row>('sync_tombstones', { empresa_id: snapshot.empresaId });
      const currentConflicts = await databaseService.listItems<Row>('sync_conflicts', { empresa_id: snapshot.empresaId });
      const currentArchives = await databaseService.listItems<Row>('accounted_pending_operations', { item_id: pending.item_id });
      const currentOwnNote = authorizedSuccessorObservation(pending, value, original.payload, currentArchives, resolution.preparedRemote);
      if (ownsNote && !currentOwnNote) return 'pending';
      const currentIntents = withoutSuccessorObservationIntent(currentOperations, currentConflicts, conflict, original.payload, currentOwnNote,
        [pending.campo_id, value.campo_id, value.snapshot_field_id, originalPhysicalField]);
      if (ownsNote) {
        const beforeRetry = await readRemote();
        if (!beforeRetry || beforeRetry.valor === resolution.preparedRemote.valor &&
            !authorizedSuccessorObservation(pending, value, original.payload, currentArchives, beforeRetry)) return 'pending';
      }
      if (!sameMediaRow(currentPending, pending) || !sameMediaRow(currentValue, value) || !sameMediaRow(currentParent, parent) ||
          !sameMediaRow(currentArchive, archive) || !sameMediaRow(currentConflict, conflict) ||
          currentImages.filter(row => row.item_type === 'CHECKLIST' && ids.includes(row.campo_id)).length !== 1 ||
          hasPendingMediaObservation(currentIntents.operations, value, pending, [originalPhysicalField]) || hasOpenMediaObservation(currentIntents.conflicts, value, pending, [originalPhysicalField]) ||
          hasMediaCancellation(currentConflicts, pending) || currentTombstones.some(row => ids.includes(row.entity_id)) ||
          currentOperations.some(row => row.operation === 'delete' && ids.includes(row.item_id))) return 'pending';
      for (const operation of currentOperations.filter(row => row.item_id === pending.item_id && row.operation === 'checklist_partial_update')) {
        let answers: Row;
        try { answers = typeof operation.data === 'string' ? JSON.parse(operation.data) : operation.data; } catch { return 'pending'; }
        if (!answers) return 'pending';
        const fields = Object.keys(answers).filter(fieldId => ids.includes(fieldId));
        if (fields.length && (fields.length !== 1 || operation.id !== conflict.pending_operation_id ||
            operation.user_id !== pending.user_id || operation.empresa_id !== pending.empresa_id ||
            operation.collection !== 'guincho_valores_checklist' ||
            checklistAnswerValue(answers[fields[0]]) !== pending.prepared_public_url)) return 'pending';
      }
      await assertPendingSyncSessionCurrent(snapshot);
      const result = await client.rpc(payload.expected_partner_revision !== undefined ? 'upsert_checklist_values_partner_v1' : payload.observacao !== undefined ? 'upsert_checklist_values_v3' : 'upsert_checklist_values_v2', { p_checklist_id: pending.item_id,
        p_values: [payload], p_client_build: getSyncClientBuild() });
      await assertPendingSyncSessionCurrent(snapshot);
      if (result.error) throw result.error;
      if (!Array.isArray(result.data) || result.data.length !== 1 || result.data[0].input_index !== 0 ||
          result.data[0].value_id !== value.id) return 'pending';
      const outcome = result.data[0];
      const rejected = outcome.result === 'conflict' && ['optimistic_lock_failed', 'expected_updated_at_mismatch'].includes(outcome.reason);
      if (!rejected && !['applied', 'historical'].includes(outcome.result)) return 'pending';
      resolution = { ...resolution, phase: rejected ? 'retry_conflict' : 'retry_applied', terminalResult: outcome };
      const nextData = JSON.stringify({ ...data, media_resolution: resolution });
      if (!await databaseService.updateItemIfUnchanged('accounted_pending_operations', archive.id,
        { data: nextData }, archive, true, () => isPendingSyncSessionSnapshotStateCurrent(snapshot))) return 'pending';
      archive = { ...archive, data: nextData }; data = JSON.parse(nextData);
      if (rejected) return 'pending';
    } else if (!resolution.terminalResult || !['applied', 'historical'].includes(resolution.terminalResult.result) ||
        resolution.terminalResult.value_id !== value.id || resolution.terminalResult.input_index !== 0) return 'pending';
    remote = await readRemote();
    if (!remote) return 'pending';
    if (ownsNote && (remote.valor !== pending.prepared_public_url || remote.observacao !== payload.observacao)) return 'pending';
    if (remote.valor !== pending.prepared_public_url) return resolveTerminalChecklistMedia(pending, snapshot);
    const confirmed = await storage.info(pending.path);
    await assertPendingSyncSessionCurrent(snapshot);
    if (confirmed.error || !confirmed.data) return 'pending';
  } else if (resolution.kind === 'server_wins') {
    const alternative = resolution.alternative as ChecklistMediaAlternative;
    if (!alternative || !sameMediaIdentityAndImage(alternative.chosenRemote, remote)) return 'pending';
    const chosen = await inspectChosenObject(pending, remote, snapshot);
    if (!chosen || !resolution.chosenObject || !sameMediaRow(chosen, resolution.chosenObject) || !await alternativeCopyMatches(alternative)) return 'pending';
  } else return 'pending';
  const fresh = await readRemote();
  if (!fresh || !sameMediaRow(fresh, remote)) return 'pending';
  const committed = await databaseService.commitChecklistMediaConflict({ pending, value, parent, archive, conflict, remote,
    resolution: { ...resolution, actor: snapshot.userId,
      ...(resolution.alternative ? { alternative: { ...resolution.alternative, resolutionActor: snapshot.userId } } : {}),
      phase: 'committed', confirmedRemote: remote },
    canCommit: () => isPendingSyncSessionSnapshotStateCurrent(snapshot) });
  if (committed) publishChecklistMediaTerminal({ companyId: snapshot.empresaId, checklistId: pending.item_id,
    fieldId: pending.campo_id, expectedValue: value.valor, localUri: pending.local_uri, uri: remote.valor,
    expectedObservation: value.observacao ?? null, observation: remote.observacao ?? null });
  return committed ? 'resolved' : 'pending';
}
